import {
  IServiceAuditorReport,
  ServiceAuditorFailureReason,
  ServiceAuditorStageName,
} from '@commenda/api/agent-platform/service-auditor/types';

import { ServiceAuditRunStatusHelper } from './ServiceAuditRunStatusHelper';

export type TServiceAuditFailure = Pick<
  IServiceAuditorReport,
  'failureReason' | 'draftStepName' | 'failedStepName'
>;

export class ServiceAuditFailureHelper {
  static readonly FAILED_TITLE = 'Audit failed';

  // Every action named here can be done from the request page: review in the approval step, or reset the upload step.
  static readonly REVIEW_OR_RESET_ACTION =
    'Review the draft by hand in the approval step, or reset the upload step and upload the draft again to start a new audit.';

  // Reviewer sentences for the backend's failure reasons. NO_NEW_DRAFT names the step, and OTHER uses the generic copy.
  static readonly FAILURE_REASON_COPY: Readonly<
    Record<
      Exclude<
        ServiceAuditorFailureReason,
        | ServiceAuditorFailureReason.NO_NEW_DRAFT
        | ServiceAuditorFailureReason.OTHER
      >,
      string
    >
  > = {
    [ServiceAuditorFailureReason.NO_DRAFT_IDENTIFIED]:
      'The auditor could not tell which file is the draft. Ask a workflow admin to set Draft comes from on the auditor step, then reset the upload step and upload the draft again.',
    [ServiceAuditorFailureReason.DRAFT_UNREADABLE]: `The auditor could not read the draft file. Check that it opens. ${ServiceAuditFailureHelper.REVIEW_OR_RESET_ACTION}`,
    [ServiceAuditorFailureReason.REQUEST_UNAVAILABLE]:
      'The request, its company or the draft file could not be found, so the audit could not run. Ask engineering to check the request.',
    [ServiceAuditorFailureReason.QUEUED_TOO_LONG]: `The audit waited too long in the queue and never started. ${ServiceAuditFailureHelper.REVIEW_OR_RESET_ACTION}`,
    [ServiceAuditorFailureReason.TIMED_OUT]: `The audit took too long and was stopped. ${ServiceAuditFailureHelper.REVIEW_OR_RESET_ACTION}`,
    [ServiceAuditorFailureReason.STOPPED_BY_RESTART]: `The audit was interrupted, for example by a deploy. ${ServiceAuditFailureHelper.REVIEW_OR_RESET_ACTION}`,
    [ServiceAuditorFailureReason.REPLACED]:
      'A newer audit of this step replaced this one.',
    [ServiceAuditorFailureReason.WORKFLOW_RESET]:
      'The workflow was reset while the audit ran, so the audit was stopped. A new audit starts when the workflow reaches this step again.',
    [ServiceAuditorFailureReason.WORKFLOW_STOPPED]:
      'The workflow was stopped while the audit ran, so the audit was stopped.',
    [ServiceAuditorFailureReason.TURN_LIMIT]: `The auditor reached its work limit before it finished. ${ServiceAuditFailureHelper.REVIEW_OR_RESET_ACTION}`,
    [ServiceAuditorFailureReason.ANSWER_CUT_OFF]: `The auditor's answer was cut off before it finished. ${ServiceAuditFailureHelper.REVIEW_OR_RESET_ACTION}`,
  };

  // One message under the failed heading: the reason's sentence when the backend sent one, else the generic body.
  static getFailedMessage({
    failureReason,
    draftStepName,
    failedStepName,
  }: Readonly<TServiceAuditFailure>): string {
    switch (failureReason) {
      case ServiceAuditorFailureReason.NO_NEW_DRAFT:
        return ServiceAuditFailureHelper.getNoNewDraftMessage(draftStepName);
      case ServiceAuditorFailureReason.OTHER:
      case null:
        return ServiceAuditFailureHelper.getFailedBody(failedStepName);
      default:
        return (
          ServiceAuditFailureHelper.FAILURE_REASON_COPY[failureReason] ??
          ServiceAuditFailureHelper.getFailedBody(failedStepName)
        );
    }
  }

  private static getNoNewDraftMessage(draftStepName: string | null): string {
    const stepName = draftStepName?.trim();
    const notFound = stepName
      ? `The auditor couldn't find the draft on “${stepName}”.`
      : "The auditor couldn't find a new draft on the upload step.";
    return `${notFound} Reset that step and upload the draft again to start a new audit.`;
  }

  private static getFailedBody(
    failedStepName: ServiceAuditorStageName | null
  ): string {
    if (!failedStepName) {
      return `The audit could not start. ${ServiceAuditFailureHelper.REVIEW_OR_RESET_ACTION}`;
    }
    // An older run can name the removed double check stage, which has no row here.
    const stageLabel =
      ServiceAuditRunStatusHelper.STAGE_ROWS[failedStepName]?.label;
    const stopped = stageLabel
      ? `The audit stopped at ${stageLabel}.`
      : 'The audit stopped.';
    return `${stopped} ${ServiceAuditFailureHelper.REVIEW_OR_RESET_ACTION}`;
  }
}
